import React, { useState, useRef, useEffect } from "react";
import { formatPrice } from "../Functions";
import productHandler from "../Functions/product-handler";
import { toast } from "react-toastify";
import productPlaceholder from "../Assets/images/product_placeholder.png";
import ModalHeader from "../Common/ModalHeader";

// Standalone product-detail modal for the "Add to Cart" spot. It owns two
// screens driven by local state:
//   detail    — product image + info, mandatory pill / Add to Cart, the
//               Related Products accordion, and a sticky "N items added" bar
//   mandatory — the product's mandatory products (getmandatorypublicproducts)
// The product (with its MandatoryProducts and child_sku) is fetched upstream
// and passed in; the modal only performs cart writes via productHandler.
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProductDetailModal = ({ product, pdf, onClose, fetchCartCounterData }) => {
  // detail | mandatory
  const [screen, setScreen] = useState("detail");
  // Related products already added, keyed by sku_code.
  const [addedRelated, setAddedRelated] = useState({});
  // Whether the main product (with its mandatory products) has been added.
  const [mainAdded, setMainAdded] = useState(false);
  // Open state of the "Related Products" accordion.
  const [relatedOpen, setRelatedOpen] = useState(false);
  const cart = useRef();

  useEffect(() => {
    cart.current = new productHandler(pdf);
  }, [pdf]);

  const related = product?.child_sku || [];
  const mandatory = product?.MandatoryProducts || [];

  // Total shown next to the CTA = the product's unit price plus every
  // mandatory product (price × quantity), matching the Figma "₹5,000 → ₹23,000".
  const unitPrice = Number(product?.product_price) || 0;
  const totalPrice =
    unitPrice +
    mandatory.reduce(
      (sum, m) => sum + (Number(m?.product_price) || 0) * (Number(m?.quantity) || 1),
      0
    );

  // Running tally shown in the sticky bottom bar: the main product (once added)
  // plus every related product added from the accordion.
  const addedCount =
    (mainAdded ? 1 : 0) +
    Object.values(addedRelated).filter(Boolean).length;

  const ensureCart = () => {
    if (!cart.current) cart.current = new productHandler(pdf);
    return cart.current;
  };

  // Adds the main product plus its mandatory products (attached to the parent
  // cart item), then marks it added so the CTA and the bottom bar update.
  const addMainToCart = async () => {
    if (mainAdded) return;
    try {
      const res = await ensureCart().AddToCart(
        [product.sku_code],
        product?.pQunatity ? product.pQunatity : 1,
        "",
        false,
        0
      );
      if (!res?.data?.success) {
        toast.error("Something went wrong!", { className: "toast-style" });
        return;
      }

      const parentId = res?.data?.data?._id || 0;
      if (mandatory.length > 0) {
        await Promise.all(
          mandatory.map((m) =>
            ensureCart()
              .AddToCart([m.sku_code], m.quantity ? m.quantity : 1, "", true, parentId)
              .catch((err) => console.error(err))
          )
        );
      }

      await fetchCartCounterData?.();
      setMainAdded(true);
    } catch (error) {
      toast.error("Something went wrong!", { className: "toast-style" });
    }
  };

  const addRelatedToCart = async (item) => {
    try {
      const res = await ensureCart().AddToCart(
        [item.sku_code],
        item?.pQunatity ? item.pQunatity : 1,
        "",
        false,
        0
      );
      if (!res?.data?.success) {
        toast.error("Something went wrong!", { className: "toast-style" });
        return;
      }
      await fetchCartCounterData?.();
      setAddedRelated((prev) => ({ ...prev, [item.sku_code]: true }));
    } catch (error) {
      toast.error("Something went wrong!", { className: "toast-style" });
    }
  };

  // ---- Mandatory products screen ----
  if (screen === "mandatory") {
    return (
      <div className="atcModal">
        <ModalHeader
          title={`Mandatory Products(${mandatory.length})`}
          onBack={() => setScreen("detail")}
          onClose={onClose}
        />
        <div className="atcBody">
          <div className="atcMandatoryList">
            {mandatory.map((m, i) => (
              <div className="atcMandatoryItem" key={i}>
                <div className="atcMandatoryImg">
                  <img
                    src={m?.product_image || productPlaceholder}
                    onError={(e) => {
                      e.target.src = productPlaceholder;
                    }}
                    alt={m?.product_name}
                  />
                </div>
                <div className="atcMandatoryInfo">
                  <h4>{m?.product_name}</h4>
                  <p className="atcSku">{m?.sku_code}</p>
                  <p className="atcDesc">{m?.product_description}</p>
                </div>
                <div className="atcMandatoryPrice">
                  <span className="amt">&#8377;{formatPrice(m?.product_price)}</span>
                  <span className="suffix"> /unit</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="atcFooter atcFooterEnd">
          <button
            type="button"
            className="atcDoneBtn"
            onClick={() => setScreen("detail")}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ---- Product detail screen (default) ----
  return (
    <div className="atcModal">
      <ModalHeader title="Add to Cart" onClose={onClose} />
      <div className="atcBody">
        <div className="atcSingleMain">
          <div className="atcSingleImg">
            <img
              src={product?.product_image || productPlaceholder}
              onError={(e) => {
                e.target.src = productPlaceholder;
              }}
              alt={product?.product_name}
            />
          </div>
          <div className="atcSingleInfo">
            <div className="atcSingleInfoTop">
              <h3 className="atcName">{product?.product_name}</h3>
              <p className="atcSku">{product?.sku_code}</p>
              <p className="atcUnitPrice">
                &#8377;{formatPrice(product?.product_price)}
                <span className="atcUnitSuffix"> /unit</span>
              </p>
              <p className="atcDesc">{product?.product_description}</p>
            </div>
            <div className="atcSingleInfoBottom">
              {mandatory.length > 0 && (
                <button
                  type="button"
                  className="atcMandatoryPill"
                  onClick={() => setScreen("mandatory")}
                >
                  <span className="atcMandatoryPillIcon">
                    <InfoIcon />
                  </span>
                  Includes +{mandatory.length} Mandatory Products
                </button>
              )}
              <p className="atcTotalPrice">
                &#8377;{formatPrice(totalPrice)}
                <span className="atcUnitSuffix"> /unit</span>
              </p>
              {/* <button
                type="button"
                className={`atcAddToCartBtn${mainAdded ? " added" : ""}`}
                disabled={mainAdded}
                onClick={addMainToCart}
              >
                {mainAdded ? "Added ✓" : "Add to Cart"}
              </button> */}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="atcAccordion">
            <div
              className="atcAccordionHeader"
              onClick={() => setRelatedOpen((prev) => !prev)}
            >
              <span className="atcAccordionTitle">
                Related Products({related.length})
              </span>
              <span className={`atcAccordionChevron ${relatedOpen ? "open" : ""}`}>
                <ChevronIcon />
              </span>
            </div>
            {relatedOpen && (
              <div className="atcAccordionBody">
                {related.map((item, i) => {
                  const isAdded = addedRelated[item.sku_code];
                  return (
                    <div className="atcRelatedItem" key={i}>
                      <div className="atcRelatedImg">
                        <img
                          src={item?.product_image || productPlaceholder}
                          onError={(e) => {
                            e.target.src = productPlaceholder;
                          }}
                          alt={item?.product_name}
                        />
                      </div>
                      <div className="atcRelatedInfo">
                        <h4>{item?.product_name}</h4>
                        <p className="atcSku">{item?.sku_code}</p>
                        <p className="atcDesc">{item?.product_description}</p>
                      </div>
                      <div className="atcRelatedPrice">
                        &#8377;{formatPrice(item?.product_price)}
                      </div>
                      {/* <button
                        type="button"
                        className={`atcAddToCartBtn small${isAdded ? " added" : ""}`}
                        disabled={isAdded}
                        onClick={() => addRelatedToCart(item)}
                      >
                        {isAdded ? "Added ✓" : "Add to Cart"}
                      </button> */}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="atcFooter">
        <span className="atcItemsAdded">
          {/* {addedCount} item{addedCount === 1 ? "" : "s"} added */}
        </span>
        <button type="button" className="atcDoneBtn" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
};

export default ProductDetailModal;
